import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { authHeaders } from "../lib/auth.js";
import { onPreviewUpdate } from "../lib/previewEvents.js";
import { trpc } from "../utils/trpc.js";
import { VisualProjectEditor } from "./VisualProjectEditor.js";

type Props = {
  projectId: number;
  deployUrl?: string | null;
  enabled?: boolean;
  browserPreviewUrl?: string | null;
};

export function BuildLivePreview({
  projectId,
  deployUrl,
  enabled = true,
  browserPreviewUrl,
}: Props) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [hmrFlash, setHmrFlash] = useState(false);
  const [designMode, setDesignMode] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: devStatus } = useQuery({
    queryKey: ["sandbox", "devStatus", projectId],
    queryFn: () => trpc.sandbox.devStatus.query({ projectId }),
    enabled: enabled && projectId > 0 && !browserPreviewUrl,
    refetchInterval: 5000,
  });

  const useDevServer = !!devStatus?.running && !deployUrl && !browserPreviewUrl;

  const {
    data: previewAuthReady,
    error: previewAuthError,
    isLoading: previewAuthLoading,
  } = useQuery({
    queryKey: ["sandbox", "previewAuth", projectId],
    queryFn: async () => {
      const response = await fetch(`/api/preview-auth/${projectId}`, {
        method: "GET",
        credentials: "same-origin",
        headers: authHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Preview authorization failed: ${response.status}`);
      }
      return true;
    },
    enabled: enabled && projectId > 0 && useDevServer,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (!enabled || projectId <= 0 || useDevServer) return;
    return onPreviewUpdate((detail) => {
      if (detail.projectId !== projectId) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setRefreshKey((k) => k + 1);
        setHmrFlash(true);
        setTimeout(() => setHmrFlash(false), 600);
      }, 400);
    });
  }, [projectId, enabled, useDevServer]);

  if (!enabled || projectId <= 0) {
    return (
      <p className="text-slate-400 text-sm">
        Preview appears when the build has generated files.
      </p>
    );
  }

  if (useDevServer && previewAuthLoading) {
    return (
      <p className="text-slate-400 text-sm">Preparing secure live preview…</p>
    );
  }

  if (useDevServer && previewAuthError) {
    return (
      <p className="text-red-400 text-sm">
        Live preview authorization failed. Refresh the page or sign in again.
      </p>
    );
  }

  const previewSrc = deployUrl
    ? deployUrl
    : browserPreviewUrl
      ? browserPreviewUrl
      : useDevServer && previewAuthReady
        ? `/sandbox-dev/${projectId}/`
        : `/apps/${projectId}?v=${refreshKey}`;

  return (
    <div className="flex flex-col gap-3 min-h-[420px]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          {deployUrl
            ? "Deployed preview"
            : browserPreviewUrl
              ? "Live workspace preview — running in your browser"
              : useDevServer
                ? "Live dev server — connected to sandbox npm run dev (HMR when running)"
                : "Static preview — auto-refreshes when Monaco, chat, or sandbox edits files"}
          {hmrFlash && (
            <span className="ml-2 text-green-400 animate-pulse">● live</span>
          )}
        </p>
        <div className="flex gap-2">
          {!deployUrl && !useDevServer && !browserPreviewUrl && (
            <button
              type="button"
              onClick={() => setDesignMode((value) => !value)}
              className="text-xs bg-blue-700 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg"
            >
              {designMode ? "Exit design" : "Design"}
            </button>
          )}
          {!useDevServer && (
            <button
              type="button"
              onClick={() => setRefreshKey((k) => k + 1)}
              className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-lg"
            >
              Refresh preview
            </button>
          )}
          <a
            href={previewSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded-lg"
          >
            Open in tab
          </a>
        </div>
      </div>
      {designMode && !deployUrl && !useDevServer && !browserPreviewUrl ? (
        <VisualProjectEditor projectId={projectId} enabled={enabled} />
      ) : (
        <iframe
          key={previewSrc}
          src={previewSrc}
          title="App preview"
          referrerPolicy="no-referrer"
          className="flex-1 w-full min-h-[380px] rounded-lg border border-slate-700 bg-white"
          sandbox="allow-scripts allow-forms allow-modals allow-popups allow-same-origin"
        />
      )}
    </div>
  );
}
